import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";
import { RootState } from "us.helper/types";
import { IClosureCodeHistory } from "us.collection.admin/components/ClosureCodes/Interfaces";
import { $Skeleton, $TableTree, $Button, $Tooltip } from "us.common/components";
import * as Actions from "us.collection.admin/actions";
import { getSelectedContent } from "../../Functions";

/**
 * @description - Closure Code change history view component
 * @author Tharanga Niroshana <tharangan@unicorn-solutions.com>
 * @since 11/07/2024
 */

const ClosureCodeHistory: React.FC<IClosureCodeHistory & PropsFromRedux> = (
  props
) => {
  const { t } = useTranslation();

  const {
    closureCodeList,
    onCancel,
    drawerDetails,
    getClosureCodeHistory,
    closureCodeHistory,
  } = props;
  const { codeId } = drawerDetails ?? {};
  const { data, isLoading } = closureCodeHistory ?? {};

  useEffect(() => {
    getClosureCodeHistory && getClosureCodeHistory({ codeId });
  }, []);

  const columns: any = [
    {
      title: t("US.COLLECTION.ADMIN:MANAGE_CLOSURE_CODES.UPDATED_BY"),
      dataIndex: "modifiedUser",
      key: "modifiedUser",
      ellipsis: true,
      customRenderChild: (text: any, record: any) => {
        return (
          <$Tooltip
            placement="topLeft"
            title={record?.modifiedUser}
            key={record?.modifiedUser}
          >
            {record?.modifiedUser}
          </$Tooltip>
        );
      },
    },
    {
      title: t("US.COLLECTION.ADMIN:MANAGE_CLOSURE_CODES.ARCHIVE_IN"),
      dataIndex: "archiveIn",
      key: "archiveIn",
      width: "130px",
      customRenderChild: (text: any, record: any) => {
        return (
          <span>
            {record?.archiveDuration} {record?.archiveDurationPeriod}
          </span>
        );
      },
    },
    {
      title: t("US.COLLECTION.ADMIN:MANAGE_CLOSURE_CODES.DELETE_IN"),
      dataIndex: "deleteIn",
      key: "deleteIn",
      width: "130px",
      customRenderChild: (text: any, record: any) => {
        return (
          <span>
            {record?.deletionDuration} {record?.deletionDurationPeriod}
          </span>
        );
      },
    },
    {
      title: t("US.COLLECTION.ADMIN:MANAGE_CLOSURE_CODES.ARCHIVE_FROM"),
      dataIndex: "archiveFrom",
      key: "archiveFrom",
      width: "170px",
      customRenderChild: (text: any, record: any) => {
        return (
          <span>
            {record?.isStartFromCaseClosedDate
              ? t("US.COLLECTION.ADMIN:MANAGE_CLOSURE_CODES.CASE_CLOSE_DATE")
              : t(
                  "US.COLLECTION.ADMIN:MANAGE_CLOSURE_CODES.NEXT_CALENDAR_PERIOD"
                )}
          </span>
        );
      },
    },
    {
      title: t("US.COLLECTION.ADMIN:MANAGE_CLOSURE_CODES.BAD_DEBT_RECORD"),
      dataIndex: "badDebtRecord",
      key: "badDebtRecord",
      width: "170px",
    },
  ];

  return (
    <>
      <div>
        <small>
          <strong>
            {t("US.COLLECTION.ADMIN:MANAGE_CLOSURE_CODES.DISPLAY_NAME_ENGLISH")}
          </strong>
        </small>
      </div>
      <div>
        {getSelectedContent(closureCodeList?.data, codeId)?.description}
      </div>

      <$Skeleton
        loading={isLoading}
        active
        paragraph={{
          rows: 2,
        }}
      >
        <$TableTree
          rowKey={(record: any) => record.id}
          data={data}
          size="small"
          filterOnType
          bordered
          className="mt-3"
          onSort={(sortData, dataSource) => {
            return sortData(dataSource);
          }}
          onFilter={(searchData, dataSource) => {
            return searchData(dataSource);
          }}
          pagination={false}
          scroll={{ x: "100%", y: "calc(100vh - 265px)" }}
          columns={columns}
          locale={{
            filterConfirm: t("COMMON.OK"),
            filterReset: t("COMMON.RESET"),
          }}
          resetOnSourceChange={true}
          virtual={true}
        />
      </$Skeleton>

      <div className="drawer-footer-fixed align-content-center justify-content-end">
        <div>
          <$Button onClick={() => onCancel()}>
            {t("US.COLLECTION.COMMON:COMMON.CANCEL")}
          </$Button>
        </div>
      </div>
    </>
  );
};

const mapStateToProps = (state: RootState) => {
  const { closureCodes } = state;
  const { closureCodeList, drawerDetails, closureCodeHistory } = closureCodes;
  return {
    closureCodeList,
    drawerDetails,
    closureCodeHistory,
  };
};

const { newClosureCode, closureCodeHistory } = Actions.closureCodes;

const mapDispatchToProps = {
  openDrawer: newClosureCode.openDrawer,
  getClosureCodeHistory: closureCodeHistory.get,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(ClosureCodeHistory);
